import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Typography,
  Collapse
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import PaletteIcon from '@mui/icons-material/Palette';
import StraightenIcon from '@mui/icons-material/Straighten';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import StyleIcon from '@mui/icons-material/Style';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BuildIcon from '@mui/icons-material/Build';
import BarChartIcon from '@mui/icons-material/BarChart';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import DescriptionIcon from '@mui/icons-material/Description';

const drawerWidth = 240;

const Sidebar = ({ mobileOpen, handleDrawerToggle }) => {
  const location = useLocation();
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [marketplaceOpen, setMarketplaceOpen] = useState(false);

  // Auto-expand submenu based on current path
  useEffect(() => {
    const inventoryPaths = ['/products', '/variants', '/sale-return', '/inventory/location', '/inventory/pick-list', '/inventory/vendor-statements', '/transactions'];
    const marketplacePaths = ['/marketplace/amazon', '/marketplace/flipkart', '/marketplace/myntra', '/marketplace/ajio', '/marketplace/nykaa', '/marketplace/return-entry-log', '/marketplace/return-entry-log'];
    
    if (inventoryPaths.some(path => location.pathname === path)) {
      setInventoryOpen(true);
    }
    
    if (marketplacePaths.some(path => location.pathname === path)) {
      setMarketplaceOpen(true);
    }
  }, [location.pathname]);

  const handleInventoryClick = () => {
    setInventoryOpen(!inventoryOpen);
  };

  const handleMarketplaceClick = () => {
    setMarketplaceOpen(!marketplaceOpen);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };
  
  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },    
    { text: 'Inventory', 
      icon: <InventoryIcon />, 
      hasSubmenu: true,
      selected: ['/products', '/variants', '/sale-return', '/inventory/location', '/inventory/vendor-statements', '/transactions'].includes(location.pathname),
      onClick: handleInventoryClick,
      open: inventoryOpen,
      submenuItems: [
        { text: 'Products', icon: <ShoppingBasketIcon />, path: '/products' },
        { text: 'Variants', icon: <StyleIcon />, path: '/variants' },
        { text: 'Location', icon: <LocationOnIcon />, path: '/inventory/location' },
        { text: 'Sale & Return', icon: <LocalShippingIcon />, path: '/sale-return' },
        { text: 'Pick List', icon: <ReceiptLongIcon />, path: '/inventory/pick-list' },
        { text: 'Vendor A/C', icon: <DescriptionIcon />, path: '/inventory/vendor-statements' }
      ]
    },
    { text: 'Marketplace', 
      icon: <StorefrontIcon />, 
      hasSubmenu: true,
      selected: ['/marketplace/amazon', '/marketplace/flipkart', '/marketplace/myntra', '/marketplace/ajio', '/marketplace/nykaa', '/marketplace/return-entry-log'].includes(location.pathname),
      onClick: handleMarketplaceClick,
      open: marketplaceOpen,
      submenuItems: [
        { text: 'Amazon', icon: <ShoppingCartIcon />, path: '/marketplace/amazon' },
        { text: 'Flipkart', icon: <ShoppingCartIcon />, path: '/marketplace/flipkart' },
        { text: 'Myntra', icon: <ShoppingCartIcon />, path: '/marketplace/myntra' },
        { text: 'Ajio', icon: <ShoppingCartIcon />, path: '/marketplace/ajio' },
        { text: 'Nykaa', icon: <ShoppingCartIcon />, path: '/marketplace/nykaa' },
        { text: 'Return Entry', icon: <AssignmentReturnIcon />, path: '/marketplace/return-entry-log' }
      ]
    },
    { text: 'Analytics', icon: <BarChartIcon />, path: '/analytics', selected: location.pathname.startsWith('/analytics') },
    { text: 'Utility', icon: <BuildIcon />, path: '/utility', selected: location.pathname === '/utility' }
    
  ];

  const bottomMenuItems = [
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    { text: 'Logout', icon: <LogoutIcon />, path: '/logout' },
  ];
  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#3f51b5' }}>
          Inventory MS
        </Typography>
      </Box>
      <List sx={{ flexGrow: 1 }}>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            <ListItem disablePadding>
              <ListItemButton 
                component={item.hasSubmenu ? 'div' : Link}
                to={item.hasSubmenu ? undefined : item.path}
                onClick={(e) => {
                  item.onClick && item.onClick(e);
                  if (!item.hasSubmenu) {
                    // Close drawer on mobile when clicking a non-submenu item
                    if (window.innerWidth < 600) {
                      handleDrawerToggle();
                    }
                  }
                }}
                sx={{ 
                  bgcolor: item.selected ? 'rgba(63, 81, 181, 0.08)' : 'transparent',
                  color: item.selected ? '#3f51b5' : 'inherit',
                  '&:hover': {
                    bgcolor: item.selected ? 'rgba(63, 81, 181, 0.12)' : 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: item.selected ? '#3f51b5' : 'inherit'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
                {item.hasSubmenu && (item.open ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>
            </ListItem>
            
            {item.hasSubmenu && (
              <Collapse in={item.open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.submenuItems.map((subItem) => (
                    <ListItemButton 
                      key={subItem.text}
                      component={Link}
                      to={subItem.path}
                      onClick={() => {
                        // Close drawer on mobile when clicking a submenu item
                        if (window.innerWidth < 600) {
                          handleDrawerToggle();
                        }
                      }}
                      sx={{ 
                        pl: 4,
                        bgcolor: isActive(subItem.path) ? 'rgba(63, 81, 181, 0.08)' : 'transparent',
                        color: isActive(subItem.path) ? '#3f51b5' : 'inherit',
                        '&:hover': {
                          bgcolor: isActive(subItem.path) ? 'rgba(63, 81, 181, 0.12)' : 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      <ListItemIcon
                        sx={{ 
                          color: isActive(subItem.path) ? '#3f51b5' : 'inherit'
                        }}
                      >
                        {subItem.icon}
                      </ListItemIcon>
                      <ListItemText primary={subItem.text} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
      
      <Divider />
      
      <List>
        {bottomMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton 
              component={Link} 
              to={item.path}
              onClick={() => {
                // Close drawer on mobile when clicking a bottom menu item
                if (window.innerWidth < 600) {
                  handleDrawerToggle();
                }
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      aria-label="navigation"
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            backgroundColor: '#fff',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          },
        }}
      >
        {drawer}
      </Drawer>
      
      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            backgroundColor: '#fff',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            border: 'none'
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
